import dayjs from 'dayjs';
import { Lease } from '@urbanmix-tech/shared-js';
import { convertPseudoDateToRealDate } from './common';

export type LeaseCategory = 'active' | 'future' | 'past' | 'draft';

export function getLeaseCategory(lease: Lease): LeaseCategory {
  if ((lease as any).status === 'draft') {
    return 'draft';
  }

  const now = dayjs().startOf('day');
  const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
 
  if (!startDate || !endDate) {
    return 'past';
  }

  const start = dayjs(startDate).startOf('day');
  const end = dayjs(endDate).startOf('day');

  if (now.isBefore(start)) {
    return 'future';
  } else if (now.isAfter(end)) {
    return 'past';
  } else {
    return 'active';
  }
}

export function getCategoryColor(category: LeaseCategory): string {
  switch (category) {
    case 'active':
      return '#4CAF50'; // green
    case 'future':
      return '#2196F3'; // blue
    case 'past':
      return '#9E9E9E'; // gray
    case 'draft':
      return '#FF9800'; // orange
    default:
      return '#666666';
  }
}

export function getCategoryLabel(category: LeaseCategory): string {
  switch (category) {
    case 'active':
      return 'Active';
    case 'future':
      return 'Future';
    case 'past':
      return 'Past';
    case 'draft':
      return 'Draft';
    default:
      return 'Unknown';
  }
}

/**
 * Calculate lease length in months
 * Rounds to nearest month if within ~3 days of a full month
 */
export function calculateLeaseLength(lease: Lease): number {
  const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);

  if (!startDate || !endDate) return 0;

  const exactMonths = dayjs(endDate).diff(dayjs(startDate), 'month', true);

  // Round to nearest month if within ~3 days (0.1 of a month ≈ 3 days)
  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return roundedMonths;
  }

  return exactMonths;
}

/**
 * Format lease length from dates for display.
 * Returns "X months" for whole months or "Xm Yd" for non-whole.
 */
export function formatLeaseLengthFromDates(startDate: Date | null, endDate: Date | null): string {
  if (!startDate || !endDate) return '0';
  const start = dayjs(startDate);
  const end = dayjs(endDate);
  const exactMonths = end.diff(start, 'month', true);

  const roundedMonths = Math.round(exactMonths);
  const difference = Math.abs(exactMonths - roundedMonths);

  if (difference <= 0.1) {
    return `${roundedMonths} months`;
  }

  const wholeMonths = Math.floor(exactMonths);
  const remainderStart = start.add(wholeMonths, 'month');
  const remainingDays = end.diff(remainderStart, 'day');

  return `${wholeMonths}m ${remainingDays}d`;
}

/**
 * Format lease length for display from a Lease object.
 * Delegates to formatLeaseLengthFromDates after extracting dates.
 */
export function formatLeaseLength(lease: Lease): string {
  const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);
  if (!startDate || !endDate) return '0 months';
  return formatLeaseLengthFromDates(startDate, endDate);
}

/**
 * Format a numeric lease length value for display.
 * Returns "X" for whole months or "Xm Yd" for fractional.
 */
export function formatLeaseLengthValue(months: number): string {
  if (months === 0) return '0';
  const rounded = Math.round(months);
  if (Math.abs(months - rounded) <= 0.1) {
    return `${rounded} months`;
  }
  const wholeMonths = Math.floor(months);
  const start = dayjs();
  const end = start.add(wholeMonths, 'month');
  const totalDaysInFraction = start.add(wholeMonths + 1, 'month').diff(end, 'day');
  const remainingDays = Math.round((months - wholeMonths) * totalDaysInFraction);
  return `${wholeMonths}m ${remainingDays}d`;
}

/**
 * Calculate total fees from lease
 */
export function calculateTotalFees(lease: Lease): number {
  return Object.values(lease.fees || {}).reduce((sum, fee) => sum + fee, 0);
}

/**
 * Calculate total concessions amount
 * Prefers using the pre-calculated totalConcessionAmount if available
 */
export function calculateTotalConcessions(lease: Lease): number {
  // Use the pre-calculated totalConcessionAmount if available
  if (lease.totalConcessionAmount !== undefined && lease.totalConcessionAmount > 0) {
    return lease.totalConcessionAmount;
  }

  // Fallback to calculating from concessions array
  if (!Array.isArray(lease.concessions)) {
    return (lease.concessions || 0) * lease.monthlyRent;
  }
  return Math.round(
    lease.concessions.reduce((sum: number, c: any) => {
      if (c.type === 'fixed') return sum + (c.amount || 0);
      if (c.type === 'percentage') return sum + ((c.amount || 0) * lease.monthlyRent / 100);
      return sum + ((c.amount || 0) * lease.monthlyRent); // rentMultiplier
    }, 0) * 100
  ) / 100;
}

/**
 * Calculate effective monthly rent (accounting for concessions)
 */
export function calculateEffectiveMonthlyRent(lease: Lease): number {
  const leaseLength = calculateLeaseLength(lease);
  const totalConcessions = calculateTotalConcessions(lease);

  if (leaseLength <= 0) return lease.monthlyRent;

  return Math.round((lease.monthlyRent - (totalConcessions / leaseLength)) * 100) / 100;
}

/**
 * Calculate annual rent per square foot
 */
export function calculateAnnualRentPerSqft(lease: Lease, sqft: number): number {
  if (sqft <= 0) return 0;
  const annualRent = lease.monthlyRent * 12;
  return Math.round((annualRent / sqft) * 100) / 100;
}

/**
 * Calculate effective annual rent per square foot
 */
export function calculateEffectiveAnnualRentPerSqft(lease: Lease, sqft: number): number {
  if (sqft <= 0) return 0;
  const effectiveMonthlyRent = calculateEffectiveMonthlyRent(lease);
  const effectiveAnnualRent = effectiveMonthlyRent * 12;
  return Math.round((effectiveAnnualRent / sqft) * 100) / 100;
}

/**
 * Check if two date ranges overlap
 */
export function dateRangesOverlap(
  start1: Date | null,
  end1: Date | null,
  start2: Date | null,
  end2: Date | null
): boolean {
  if (!start1 || !end1 || !start2 || !end2) return false;

  const s1 = dayjs(start1).startOf('day');
  const e1 = dayjs(end1).startOf('day');
  const s2 = dayjs(start2).startOf('day');
  const e2 = dayjs(end2).startOf('day');

  // Two ranges overlap if one starts before the other ends AND ends after the other starts
  return s1.isBefore(e2) && e1.isAfter(s2);
}

/**
 * Find overlapping leases for a given lease within a list of leases
 */
export function findOverlappingLeases(lease: Lease, allLeases: Lease[]): Lease[] {
  const leaseStart = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const leaseEnd = convertPseudoDateToRealDate(lease.leaseEndDate as any);

  if (!leaseStart || !leaseEnd) return [];

  return allLeases.filter((other) => {
    if (other.id === lease.id) return false;
    if (other.unitId !== lease.unitId) return false;

    // Skip canceled leases
    if ((other as any).status === 'canceled') return false;

    const otherStart = convertPseudoDateToRealDate(other.leaseStartDate as any);
    const otherEnd = convertPseudoDateToRealDate(other.leaseEndDate as any);

    return dateRangesOverlap(leaseStart, leaseEnd, otherStart, otherEnd);
  });
}

export interface LeaseConflict {
  unitId: string;
  leases: Lease[];
  conflictPairs: Array<{ lease1: Lease; lease2: Lease; overlapDays: number }>;
}

/**
 * Find all lease conflicts (overlapping leases) grouped by unit
 */
export function findAllLeaseConflicts(leases: Lease[]): LeaseConflict[] {
  // Group leases by unit
  const leasesByUnit = new Map<string, Lease[]>();
  leases.forEach((lease) => {
    // Skip canceled leases
    if ((lease as any).status === 'canceled') return;

    const unitLeases = leasesByUnit.get(lease.unitId) || [];
    unitLeases.push(lease);
    leasesByUnit.set(lease.unitId, unitLeases);
  });

  const conflicts: LeaseConflict[] = [];

  leasesByUnit.forEach((unitLeases, unitId) => {
    if (unitLeases.length < 2) return;

    const conflictPairs: Array<{ lease1: Lease; lease2: Lease; overlapDays: number }> = [];
    const conflictingLeaseIds = new Set<string>();

    // Check each pair of leases for overlap
    for (let i = 0; i < unitLeases.length; i++) {
      for (let j = i + 1; j < unitLeases.length; j++) {
        const lease1 = unitLeases[i];
        const lease2 = unitLeases[j];

        const start1 = convertPseudoDateToRealDate(lease1.leaseStartDate as any);
        const end1 = convertPseudoDateToRealDate(lease1.leaseEndDate as any);
        const start2 = convertPseudoDateToRealDate(lease2.leaseStartDate as any);
        const end2 = convertPseudoDateToRealDate(lease2.leaseEndDate as any);

        if (dateRangesOverlap(start1, end1, start2, end2)) {
          // Calculate overlap days
          const overlapStart = dayjs(start1).isAfter(dayjs(start2)) ? start1 : start2;
          const overlapEnd = dayjs(end1).isBefore(dayjs(end2)) ? end1 : end2;
          const overlapDays = dayjs(overlapEnd).diff(dayjs(overlapStart), 'day') + 1;

          conflictPairs.push({ lease1, lease2, overlapDays });
          conflictingLeaseIds.add(lease1.id);
          conflictingLeaseIds.add(lease2.id);
        }
      }
    }

    if (conflictPairs.length > 0) {
      conflicts.push({
        unitId,
        leases: unitLeases.filter((l) => conflictingLeaseIds.has(l.id)),
        conflictPairs,
      });
    }
  });

  return conflicts;
}

/**
 * Calculate new lease start and end dates based on a current lease end date and duration
 * Returns dates as YYYY-MM-DD strings to avoid timezone conversion issues
 */
export interface NewLeaseDates {
  startDate: string;
  endDate: string;
}

export interface LeaseDuration {
  months?: number;
  years?: number;
}

/**
 * Calculate new lease dates from a current lease end date
 * @param currentLeaseEndDate - The end date of the current lease (Date, string, or null)
 * @param duration - Duration object with months and/or years
 * @returns Object with startDate and endDate as YYYY-MM-DD strings
 */
export function calculateNewLeaseDates(
  currentLeaseEndDate: Date | string | null,
  duration: LeaseDuration
): NewLeaseDates {
  // Calculate total months - only default to 12 if both years and months are undefined/0
  const years = duration.years || 0;
  const months = duration.months || 0;
  const totalMonths = years * 12 + months || 12; // Default to 12 if total is 0
  
  let startDate: string;
  
  if (currentLeaseEndDate) {
    // Convert current lease end date to dayjs, ensuring no timezone issues
    const endDate = typeof currentLeaseEndDate === 'string' 
      ? dayjs(currentLeaseEndDate) 
      : dayjs(currentLeaseEndDate);
    
    // New lease starts the day after current lease ends
    startDate = endDate.add(1, 'day').format('YYYY-MM-DD');
  } else {
    // If no current lease end date, start today
    startDate = dayjs().format('YYYY-MM-DD');
  }
  
  // Calculate end date by adding duration and subtracting 1 day
  // (lease period is inclusive of start and end dates)
  const endDate = dayjs(startDate)
    .add(totalMonths, 'month')
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
  
  return {
    startDate,
    endDate
  };
}

/**
 * Calculate new lease dates from a Lease object's end date
 * @param currentLease - The current lease object
 * @param duration - Duration object with months and/or years
 * @returns Object with startDate and endDate as YYYY-MM-DD strings
 */
export function calculateNewLeaseDatesFromLease(
  currentLease: Lease,
  duration: LeaseDuration
): NewLeaseDates {
  const currentLeaseEndDate = convertPseudoDateToRealDate(currentLease.leaseEndDate as any);
  return calculateNewLeaseDates(currentLeaseEndDate, duration);
}

/**
 * Calculate new lease dates with a specific start date
 * @param startDate - The desired start date (Date, string, or null - if null, uses today)
 * @param duration - Duration object with months and/or years
 * @returns Object with startDate and endDate as YYYY-MM-DD strings
 */
export function calculateLeaseDatesFromStart(
  startDate: Date | string | null,
  duration: LeaseDuration
): NewLeaseDates {
  // Calculate total months - only default to 12 if both years and months are undefined/0
  const years = duration.years || 0;
  const months = duration.months || 0;
  const totalMonths = years * 12 + months || 12; // Default to 12 if total is 0
  
  const start = startDate 
    ? (typeof startDate === 'string' ? dayjs(startDate) : dayjs(startDate))
    : dayjs();
  
  const formattedStartDate = start.format('YYYY-MM-DD');
  
  // Calculate end date by adding duration and subtracting 1 day
  const endDate = start
    .add(totalMonths, 'month')
    .subtract(1, 'day')
    .format('YYYY-MM-DD');
  
  return {
    startDate: formattedStartDate,
    endDate
  };
}
